/* Nurse portal: overview, my bookings (visit OTP lifecycle), availability, attendance, profile. */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Clock4, UserRound,
  MapPin, Phone, FileText, AlertTriangle, X, CheckCircle2, Camera, Thermometer,
} from "lucide-react";
import config from "../../config";
import { fieldError } from "../../lib/validation";
import IceboxTempBar from "../../components/IceboxTempBar";
import { formatDateDMY, formatDateTimeDMY, greetingForNow, mapsUrlFor } from "../../lib/dates";
import WeeklyAvailabilityPanel from "../../components/WeeklyAvailabilityPanel";
import DmyDateInput from "../../components/DmyDateInput";
import {
  useStaffSession, StaffLogin, StaffShell, SectionCard, StatCard, StatusChip,
  staffHeaders, detailMessage, openAuthedFile, paymentSucceeded,
} from "./staffShared";

const UNIFORM_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

function fmtDate(d) {
  return formatDateDMY(d);
}

function fmtDateTime(iso) {
  return formatDateTimeDMY(iso);
}

function customerEmail(b) {
  const e = String(b?.customer_email || "").trim();
  if (!e || e.endsWith("@phone.bookmyjab.local")) return "";
  return e;
}

function hasValidRx(b) {
  if (!b) return false;
  if (b.has_valid_prescription === true) return true;
  if ((b.rx_reviewed_at || "").trim()) return true;
  if ((b.rx_doctor_id || "").trim()) return true;
  if ((b.doctor_notes || "").trim()) return true;
  if ((b.rx_items || []).length) return true;
  const official = (b.prescription_file_reference || "").trim();
  const patient = (b.patient_prescription_file_reference || "").trim();
  if (official && official !== patient) return true;
  return false;
}

function visitMapsUrl(b) {
  if (b?.maps_url) return b.maps_url;
  const pin = b?.visit_geo || b?.geo || {};
  if (pin.maps_url) return pin.maps_url;
  return mapsUrlFor(pin.lat, pin.lng);
}

function ObservationPanel({ booking, onCustomerFine, clearing }) {
  const start = booking.observation_started_at || booking.vaccine_administered_at;
  const cleared = booking.observation_cleared_at;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!start || cleared) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [start, cleared]);

  if (!start) return null;
  const total = 30 * 60 * 1000;
  const remaining = cleared ? 0 : Math.max(0, total - (now - new Date(start).getTime()));
  const done = remaining === 0 || Boolean(cleared);
  const mm = String(Math.floor(remaining / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");

  return (
    <div className="rounded-xl p-3 bg-[#faf8fc] border border-[#673c96]/15 space-y-1" data-testid="observation-panel">
      <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Observation time started</p>
      {done ? (
        <p className="font-semibold text-emerald-700 mt-0.5">
          {cleared ? "Customer confirmed they are fine — you may leave after ending the visit." : "30 minutes complete — confirm the customer is fine before ending the visit."}
        </p>
      ) : (
        <p className="font-semibold text-[#311b52] mt-0.5 tabular-nums text-2xl" data-testid="observation-timer">{mm}:{ss}</p>
      )}
      <p className="text-xs text-[#6b7280]">Stay on site to watch for reactions. Confirm the customer is fine before ending the visit.</p>
      {!cleared && (
        <button
          type="button"
          onClick={onCustomerFine}
          disabled={clearing}
          className="mt-2 px-3 py-1.5 rounded-full border border-[#673c96] text-[#673c96] text-xs font-semibold"
          data-testid="btn-customer-fine"
        >
          {clearing ? "Saving…" : "Customer is fine"}
        </button>
      )}
    </div>
  );
}

function todayStr() {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
}

/* ── Booking detail + visit actions ─────────────────────────────────────── */

function VisitFormModal({ booking, onClose, onDone }) {
  const [otpInfo, setOtpInfo] = useState(null);
  const [code, setCode] = useState("");
  const [batchExpiry, setBatchExpiry] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [consentPhoto, setConsentPhoto] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.post(`${config.API}/nurse/bookings/${booking.id}/start-visit/send-otp`, {}, { headers: staffHeaders() })
      .then(({ data }) => setOtpInfo(data))
      .catch((err) => setError(detailMessage(err)));
  }, [booking.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) { setError("Vaccine photo is required"); return; }
    if (!consentPhoto) { setError("Photo of the signed physical consent form is required"); return; }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("code", code.trim());
      fd.append("batch_expiry", batchExpiry);
      fd.append("notes", notes);
      fd.append("vaccine_photo", photo);
      fd.append("consent_form", consentPhoto);
      await axios.post(`${config.API}/nurse/bookings/${booking.id}/start-visit/verify`, fd, { headers: staffHeaders() });
      onDone("Visit started — status is now Ongoing");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 w-full max-w-md space-y-4 my-8" data-testid="start-visit-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Start visit</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-[#673c96]" /></button>
        </div>
        <p className="text-sm text-[#6b7280]">
          An OTP was sent to the customer{otpInfo?.phone_last4 ? ` (••••${otpInfo.phone_last4})` : ""}. Ask them for it to begin.
        </p>
        {otpInfo?.message && <p className="text-xs text-[#673c96]">{otpInfo.message}</p>}
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Customer OTP *</label>
          <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit OTP"
            className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3 tracking-widest text-center" data-testid="start-visit-otp" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Vaccine batch expiry date *</label>
          <DmyDateInput required min={todayStr()} value={batchExpiry} onChange={setBatchExpiry}
            className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="start-visit-expiry" />
          <p className="text-[11px] text-[#6b7280] mt-1">Past dates are disabled — expiry must be today or later.</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Vaccine photo *</label>
          <label className="flex items-center gap-3 rounded-xl border border-dashed border-[#673c96]/40 px-4 py-3 cursor-pointer text-sm text-[#673c96]">
            <Camera className="w-5 h-5" />
            {photo ? photo.name : "Take / choose a picture of the vaccine vial"}
            <input type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="start-visit-photo" />
          </label>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Signed physical consent form *</label>
          <label className="flex items-center gap-3 rounded-xl border border-dashed border-[#673c96]/40 px-4 py-3 cursor-pointer text-sm text-[#673c96]">
            <Camera className="w-5 h-5" />
            {consentPhoto ? consentPhoto.name : "Photo of the paper form the customer signed"}
            <input type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden"
              onChange={(e) => setConsentPhoto(e.target.files?.[0] || null)} data-testid="start-visit-consent" />
          </label>
          <p className="text-[11px] text-[#6b7280] mt-1">This is stored as the customer’s physical consent for super admin.</p>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Anything to record before starting"
            className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" />
        </div>
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="start-visit-submit">
          {loading ? "Verifying…" : "Verify OTP & start visit"}
        </button>
      </form>
    </div>
  );
}

function EndVisitModal({ booking, onClose, onDone }) {
  const [otpInfo, setOtpInfo] = useState(null);
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.post(`${config.API}/nurse/bookings/${booking.id}/end-visit/send-otp`, {}, { headers: staffHeaders() })
      .then(({ data }) => setOtpInfo(data))
      .catch((err) => setError(detailMessage(err)));
  }, [booking.id]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${config.API}/nurse/bookings/${booking.id}/end-visit/verify`,
        { code: code.trim(), notes }, { headers: staffHeaders() });
      onDone("Visit completed 🎉");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 w-full max-w-md space-y-4" data-testid="end-visit-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>End visit</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-[#673c96]" /></button>
        </div>
        <p className="text-sm text-[#6b7280]">
          A closing OTP was sent to the customer{otpInfo?.phone_last4 ? ` (••••${otpInfo.phone_last4})` : ""}. Enter it to mark this booking completed.
        </p>
        {booking.visit?.batch_expiry && (
          <p className="text-sm text-[#311b52]">Batch expiry: {fmtDate(booking.visit.batch_expiry)}</p>
        )}
        {otpInfo?.message && <p className="text-xs text-[#673c96]">{otpInfo.message}</p>}
        <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit OTP"
          className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3 tracking-widest text-center" data-testid="end-visit-otp" />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Closing notes (optional)"
          className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" />
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        <button disabled={loading} className="w-full bg-emerald-600 text-white rounded-full py-3 font-semibold" data-testid="end-visit-submit">
          {loading ? "Verifying…" : "Verify OTP & complete"}
        </button>
      </form>
    </div>
  );
}

function IssueModal({ booking, onClose, onDone }) {
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("description", description);
      if (image) fd.append("image", image);
      await axios.post(`${config.API}/nurse/bookings/${booking.id}/issues`, fd, { headers: staffHeaders() });
      onDone("Issue reported to the ops team");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 w-full max-w-md space-y-4" data-testid="issue-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Report an issue</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-[#673c96]" /></button>
        </div>
        <textarea required value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
          placeholder="Describe the problem (address not found, customer unavailable, vaccine issue…)"
          className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="issue-description" />
        <label className="flex items-center gap-3 rounded-xl border border-dashed border-[#673c96]/40 px-4 py-3 cursor-pointer text-sm text-[#673c96]">
          <Camera className="w-5 h-5" />
          {image ? image.name : "Attach a photo (optional)"}
          <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => setImage(e.target.files?.[0] || null)} />
        </label>
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        <button disabled={loading} className="w-full bg-[#311b52] text-white rounded-full py-3 font-semibold" data-testid="issue-submit">
          {loading ? "Sending…" : "Send to ops team"}
        </button>
      </form>
    </div>
  );
}

function BookingDetail({ bookingId, onClose, onChanged }) {
  const [b, setB] = useState(null);
  const [modal, setModal] = useState(null); // start | end | issue
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    axios.get(`${config.API}/nurse/bookings/${bookingId}`, { headers: staffHeaders() })
      .then(({ data }) => setB(data))
      .catch((err) => setError(detailMessage(err)));
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const arrive = async () => {
    setError("");
    try {
      await axios.post(`${config.API}/nurse/bookings/${bookingId}/administer`, {}, { headers: staffHeaders() });
      setMsg("Vaccine administered — observation time started");
      load();
      onChanged();
    } catch (err) {
      setError(detailMessage(err));
    }
  };

  const onTheWay = async () => {
    setError("");
    try {
      await axios.post(`${config.API}/nurse/bookings/${bookingId}/on-the-way`, {}, { headers: staffHeaders() });
      setMsg("Customer can now see live icebox location");
      load();
      onChanged();
    } catch (err) {
      setError(detailMessage(err));
    }
  };

  const customerFine = async () => {
    setError("");
    setClearing(true);
    try {
      await axios.post(`${config.API}/nurse/bookings/${bookingId}/observation-clear`, { reason: "customer_ok" }, { headers: staffHeaders() });
      setMsg("Customer is fine — you may leave after ending the visit");
      load();
      onChanged();
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setClearing(false);
    }
  };

  const modalDone = (m) => {
    setModal(null);
    setMsg(m);
    load();
    onChanged();
  };

  if (!b) {
    return (
      <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-3xl p-8" onClick={(e) => e.stopPropagation()}>
          {error ? <p className="text-sm text-[#ee4036]">{error}</p>
            : <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />}
        </div>
      </div>
    );
  }

  const visitStarted = b.status === "ongoing" || Boolean(b.visit?.start_verified_at);
  const administered = Boolean(b.vaccine_administered_at || b.arrived_at || b.arrived_on_site);
  const canAdminister = visitStarted && b.status === "ongoing" && !administered;
  const canOnTheWay = ["assigned", "confirmed"].includes(b.status);
  const canStart = ["on_the_way", "arrived"].includes(b.status);
  const canEnd = b.status === "ongoing";
  const observationCleared = Boolean(b.observation_cleared_at);
  const amount = b.final_amount ?? b.final_price ?? b.indicative_price;
  const rxOk = hasValidRx(b);
  const mapsHref = visitMapsUrl(b);

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 space-y-4"
        onClick={(e) => e.stopPropagation()} data-testid="nurse-booking-detail">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{b.beneficiary_name || b.customer_name}</h3>
              <StatusChip status={b.status} />
              {administered && (
                <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-800" data-testid="administered-tag">
                  Vaccine Administered
                </span>
              )}
            </div>
            <p className="text-sm text-[#673c96] mt-1">{b.service_name || b.vaccine_name}{b.vaccine_category ? ` · ${b.vaccine_category}` : ""}</p>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-[#673c96]" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-[#faf8fc] rounded-xl p-3">
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Slot</p>
            <p className="font-semibold text-[#311b52] mt-0.5">{fmtDate(b.preferred_date)}</p>
            <p className="text-[#673c96]">{b.preferred_slot || "—"}</p>
          </div>
          <div className="bg-[#faf8fc] rounded-xl p-3">
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Patient</p>
            <p className="font-semibold text-[#311b52] mt-0.5">{b.beneficiary_name || "—"}</p>
            <p className="text-[#673c96]">{b.beneficiary_age != null ? `${b.beneficiary_age} yrs` : "Age —"}</p>
            <p className="text-[#311b52] text-xs mt-1">DOB: {b.beneficiary_dob ? fmtDate(b.beneficiary_dob) : "—"}</p>
          </div>
          <div className="bg-[#faf8fc] rounded-xl p-3 col-span-2">
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70 flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</p>
            <p className="text-[#311b52] mt-0.5">{b.address || "—"}</p>
            {mapsHref && (
              <a href={mapsHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-sm font-semibold text-[#f37721]" data-testid="nurse-open-maps">
                Open in Maps
              </a>
            )}
          </div>
          <div className="bg-[#faf8fc] rounded-xl p-3">
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70 flex items-center gap-1"><Phone className="w-3 h-3" /> Customer</p>
            <p className="font-semibold text-[#311b52] mt-0.5">{b.customer_name || "—"}</p>
            {customerEmail(b) && <p className="text-sm text-[#673c96] break-all">{customerEmail(b)}</p>}
            {b.customer_phone && <a className="text-[#f37721] font-semibold" href={`tel:+${b.customer_phone}`}>Call +{b.customer_phone}</a>}
          </div>
          <div className={`rounded-xl p-3 ${b.payment_mode_display === "prepaid" ? "bg-emerald-50" : "bg-orange-50"}`}>
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Payment</p>
            {b.payment_mode_display === "prepaid" ? (
              <p className="font-semibold text-emerald-700 mt-0.5">Prepaid — nothing to collect</p>
            ) : (
              <p className="font-semibold text-[#f37721] mt-0.5">Collect ₹{Number(b.collect_amount || amount || 0).toLocaleString("en-IN")}</p>
            )}
            <p className="text-[#673c96] text-xs capitalize">{b.payment_method || ""}</p>
          </div>
        </div>

        {(b.notes || b.prescription_note || b.doctor_notes) && (
          <div className="bg-[#faf8fc] rounded-xl p-3 text-sm">
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Description / notes</p>
            {b.doctor_notes && (
              <p className="text-[#311b52] mt-0.5 font-medium" data-testid="nurse-doctor-notes">Doctor: {b.doctor_notes}</p>
            )}
            {b.notes && <p className="text-[#311b52] mt-0.5">{b.notes}</p>}
            {b.prescription_note && <p className="text-[#673c96] mt-0.5">Customer Rx note: {b.prescription_note}</p>}
          </div>
        )}

        <div className="flex items-center justify-between bg-[#faf8fc] rounded-xl p-3 text-sm">
          <div>
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Prescription</p>
            <p className="text-[#311b52] capitalize">{rxOk ? "Reviewed by doctor" : (b.prescription_status || "—").replaceAll("_", " ")}</p>
          </div>
          {(b.prescription_file_reference || b.patient_prescription_file_reference || b.rx_doctor_id) && (
            <div className="flex flex-col items-end gap-1">
              <button type="button" onClick={() => openAuthedFile(`${config.API}/nurse/bookings/${b.id}/prescription`, setError)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#673c96] underline">
                <FileText className="w-4 h-4" /> View prescription
              </button>
              {b.patient_prescription_file_reference && b.patient_prescription_file_reference !== b.prescription_file_reference && (
                <button type="button" onClick={() => openAuthedFile(`${config.API}/nurse/bookings/${b.id}/prescription?kind=patient`, setError)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#673c96] underline">
                  Patient upload
                </button>
              )}
            </div>
          )}
        </div>

        {b.visit?.batch_expiry && (
          <div className="bg-orange-50 rounded-xl p-3 text-sm">
            <p className="text-[10px] uppercase font-semibold text-[#673c96]/70">Visit record</p>
            <p className="text-[#311b52]">Batch expiry: {fmtDate(b.visit.batch_expiry)}</p>
            {b.visit.start_notes && <p className="text-[#673c96]">Notes: {b.visit.start_notes}</p>}
            {b.visit.start_verified_at && <p className="text-xs text-[#6b7280]">Started {fmtDateTime(b.visit.start_verified_at)}</p>}
          </div>
        )}

        {administered && (
          <ObservationPanel booking={b} onCustomerFine={customerFine} clearing={clearing} />
        )}

        {msg && <p className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {msg}</p>}
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        {(canOnTheWay || canStart) && !rxOk && (
          <p className="text-sm text-[#ee4036]">A valid prescription is required before On the way. The doctor must confirm the vaccine before the nurse leaves the facility.</p>
        )}
        {canEnd && administered && !observationCleared && (
          <p className="text-sm text-[#ee4036]">Confirm the customer is fine before ending the visit.</p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {canOnTheWay && (
            <button type="button" onClick={onTheWay} disabled={!rxOk}
              className="px-4 py-2.5 rounded-full bg-[#673c96] text-white text-sm font-semibold disabled:opacity-50" data-testid="btn-on-the-way">
              On the way
            </button>
          )}
          {canAdminister && (
            <button type="button" onClick={arrive}
              className="px-4 py-2.5 rounded-full bg-[#673c96] text-white text-sm font-semibold" data-testid="btn-administered">
              Vaccine Administered
            </button>
          )}
          {canStart && (
            <button type="button" onClick={() => setModal("start")} disabled={!rxOk}
              className="px-4 py-2.5 rounded-full bg-[#f37721] text-white text-sm font-semibold disabled:opacity-50" data-testid="btn-start-visit">
              Start visit
            </button>
          )}
          {canEnd && (
            <button type="button" onClick={() => setModal("end")} disabled={!observationCleared}
              className="px-4 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="btn-end-visit">
              End visit
            </button>
          )}
          <button type="button" onClick={() => setModal("issue")}
            className="px-4 py-2.5 rounded-full border border-[#ee4036]/40 text-[#ee4036] text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="btn-report-issue">
            <AlertTriangle className="w-4 h-4" /> Report issue
          </button>
        </div>

        {modal === "start" && <VisitFormModal booking={b} onClose={() => setModal(null)} onDone={modalDone} />}
        {modal === "end" && <EndVisitModal booking={b} onClose={() => setModal(null)} onDone={modalDone} />}
        {modal === "issue" && <IssueModal booking={b} onClose={() => setModal(null)} onDone={modalDone} />}
      </div>
    </div>
  );
}

function BookingCard({ b, onOpen }) {
  const amount = b.final_amount ?? b.final_price ?? b.indicative_price;
  return (
    <button type="button" onClick={() => onOpen(b.id)}
      className="w-full text-left bg-white rounded-2xl border border-[#673c96]/10 p-4 hover:border-[#673c96]/40 transition-colors"
      data-testid={`nurse-booking-${b.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{b.beneficiary_name || b.customer_name}</p>
        <StatusChip status={b.status} />
      </div>
      <p className="text-sm text-[#311b52] mt-1">
        {b.customer_name || "Customer"}
        {customerEmail(b) ? ` · ${customerEmail(b)}` : ""}
      </p>
      <p className="text-sm text-[#673c96] mt-0.5">{b.service_name || b.vaccine_name}</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-[#6b7280]">
        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {fmtDate(b.preferred_date)} · {b.preferred_slot || "—"}</span>
        <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {(b.address || "—").slice(0, 44)}{(b.address || "").length > 44 ? "…" : ""}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        {b.payment_mode_display === "prepaid" || paymentSucceeded(b.payment_status)
          ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Prepaid</span>
          : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Collect ₹{Number(amount || 0).toLocaleString("en-IN")}</span>}
        {hasValidRx(b) && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#673c96]/10 text-[#673c96]">Rx attached</span>}
        {b.date_passed && b.status !== "completed" && (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="date-passed-badge">Date passed</span>
        )}
        {(b.status === "ongoing" || b.visit?.start_verified_at) && (b.vaccine_administered_at || b.arrived_at || b.arrived_on_site) && (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Vaccine Administered</span>
        )}
      </div>
    </button>
  );
}

function BookingsSection() {
  const [tab, setTab] = useState("upcoming");
  const [todayOnly, setTodayOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { status: tab };
    if (todayOnly) params.date = todayStr();
    axios.get(`${config.API}/nurse/assignments`, { params, headers: staffHeaders() })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab, todayOnly]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <SectionCard title="My bookings" subtitle="Upcoming visits. Past dates move to Completed — unfinished ones are marked Date passed.">
        <div className="flex flex-wrap items-center gap-2">
          {[["upcoming", "Upcoming"], ["completed", "Completed"], ["cancelled", "Cancelled"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === id ? "bg-[#311b52] text-white" : "bg-[#f4f5f7] text-[#673c96]"}`}
              data-testid={`bookings-tab-${id}`}>
              {label}
            </button>
          ))}
          <label className="ml-auto inline-flex items-center gap-2 text-sm font-medium text-[#311b52] cursor-pointer">
            <input type="checkbox" checked={todayOnly} onChange={(e) => setTodayOnly(e.target.checked)}
              className="accent-[#f37721] w-4 h-4" data-testid="today-filter" />
            My bookings for today
          </label>
        </div>
      </SectionCard>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#673c96]/25 p-10 text-center text-[#673c96]">
          No {todayOnly ? "bookings for today" : `${tab} bookings`} yet.
        </div>
      ) : (
        <div className="grid gap-3">{items.map((b) => <BookingCard key={b.id} b={b} onOpen={setOpenId} />)}</div>
      )}

      {openId && <BookingDetail bookingId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

/* ── Availability ───────────────────────────────────────────────────────── */

function AvailabilitySection() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(todayStr());
  const [draftHours, setDraftHours] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    axios.get(`${config.API}/nurse/availability`, { headers: staffHeaders() })
      .then(({ data: d }) => {
        setData(d);
        setNotice(d.notice || "");
      })
      .catch((err) => setError(detailMessage(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dayMap = useMemo(() => {
    const m = {};
    (data?.days || []).forEach((d) => { m[d.date] = d.hours || []; });
    return m;
  }, [data]);

  useEffect(() => {
    setDraftHours(dayMap[selected] || []);
  }, [selected, dayMap]);

  const bookedOnSelected = useMemo(() => {
    const s = new Set();
    (data?.schedule || []).forEach((row) => {
      if (row.date === selected && row.status === "booked") s.add(row.hour);
    });
    return s;
  }, [data, selected]);

  const bookedUpcoming = useMemo(() => {
    const rows = [...(data?.schedule || [])].filter((r) => r.status === "booked");
    rows.sort((a, b) => `${a.date}${a.hour}`.localeCompare(`${b.date}${b.hour}`));
    return rows;
  }, [data]);

  const calendar = useMemo(() => {
    if (!data) return [];
    const out = [];
    const start = new Date(`${data.from}T00:00:00`);
    const end = new Date(`${data.to}T00:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const weekday = d.getDay();
      const bookable = (data.bookable?.weekdays || []).includes(weekday)
        && !(data.bookable?.blocked_dates || []).includes(iso);
      out.push({ iso, weekday, bookable, label: d.getDate(), month: d.toLocaleString("en-IN", { month: "short" }) });
    }
    return out;
  }, [data]);

  const persistHours = async (apply) => {
    setSaving(true);
    setError("");
    try {
      await axios.put(
        `${config.API}/nurse/availability`,
        { date: selected, hours: draftHours, ...(apply ? { apply } : {}) },
        { headers: staffHeaders() },
      );
      load();
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleHour = (hour) => {
    if (bookedOnSelected.has(hour)) return;
    setDraftHours((prev) => (prev.includes(hour) ? prev.filter((h) => h !== hour) : [...prev, hour].sort()));
  };

  const dismissNotice = async () => {
    try {
      await axios.post(`${config.API}/nurse/availability/ack`, {}, { headers: staffHeaders() });
    } catch { /* keep banner dismissed locally */ }
    setNotice("");
  };

  if (!data) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;
  }

  const selectedInfo = calendar.find((c) => c.iso === selected);
  const dayCounts = Object.fromEntries(Object.entries(dayMap).map(([k, v]) => [k, (v || []).length]));

  return (
    <div className="space-y-4">
      {notice && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3" data-testid="availability-notice">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-900">Hours updated</p>
            <p className="text-sm text-emerald-800 mt-0.5">{notice}</p>
          </div>
          <button type="button" onClick={dismissNotice} className="text-xs font-semibold text-emerald-800" data-testid="availability-notice-dismiss">Dismiss</button>
        </div>
      )}

      <SectionCard title="Upcoming bookings" subtitle={bookedUpcoming.length ? `${bookedUpcoming.length} visit${bookedUpcoming.length === 1 ? "" : "s"} assigned to you` : "No booked visits in this window"}>
        {bookedUpcoming.length === 0 ? (
          <p className="text-sm text-[#6b7280]">Booked visits will show here. Open hours are edited in the weekly grid below — they are not listed one-by-one.</p>
        ) : (
          <div className="divide-y divide-[#673c96]/10" data-testid="availability-schedule">
            {bookedUpcoming.map((row) => (
              <div key={`${row.date}-${row.hour}`} className="py-2.5 flex items-center gap-3">
                <div className="min-w-[88px]">
                  <p className="text-sm font-semibold text-[#311b52]">{fmtDate(row.date)}</p>
                </div>
                <p className="flex-1 text-sm text-[#311b52]">{row.label || row.hour}</p>
                <div className="text-right">
                  <span className="inline-flex px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-[11px] font-semibold">Booked</span>
                  <p className="text-[11px] text-[#6b7280] mt-0.5">{row.beneficiary_name || "Patient"}{row.service_name ? ` · ${row.service_name}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Change hours" subtitle="Pick a day, then tap the hours you can take home visits. Save when you are done.">
        <div className="grid grid-cols-7 gap-1.5 text-[10px] font-semibold text-[#673c96]/70 uppercase mb-1.5">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => <div key={w} className="text-center">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5 mb-5" data-testid="availability-grid">
          {calendar.length > 0 && Array.from({ length: calendar[0].weekday }).map((_, i) => <div key={`pad-${i}`} />)}
          {calendar.map((c) => {
            const count = (dayMap[c.iso] || []).length;
            const isSel = selected === c.iso;
            return (
              <button key={c.iso} type="button" disabled={!c.bookable}
                onClick={() => setSelected(c.iso)}
                className={`rounded-xl py-2 flex flex-col items-center border transition-colors ${
                  isSel ? "bg-[#673c96] text-white border-[#673c96]"
                    : !c.bookable ? "bg-[#f4f5f7] text-[#c2b8d6] border-transparent cursor-not-allowed"
                    : count > 0 ? "bg-emerald-50 text-[#311b52] border-emerald-200"
                    : "bg-white text-[#311b52] border-[#673c96]/15 hover:border-[#673c96]/50"
                }`}
                data-testid={`avail-day-${c.iso}`}>
                <span className="text-sm font-semibold">{c.label}</span>
                <span className={`text-[9px] ${isSel ? "text-white/80" : "text-[#673c96]/70"}`}>{c.label === 1 || c.iso === data.from ? c.month : ""}</span>
                {count > 0 && <span className={`mt-0.5 text-[9px] font-bold ${isSel ? "text-white" : "text-emerald-600"}`}>{count}</span>}
              </button>
            );
          })}
        </div>
        <WeeklyAvailabilityPanel
          selected={selected}
          onSelectDay={setSelected}
          slotHours={data.slot_hours || []}
          hours={draftHours}
          bookedHours={[...bookedOnSelected]}
          dayCounts={dayCounts}
          onToggleHour={toggleHour}
          onSave={() => persistHours()}
          onApplyWeek={() => persistHours("week")}
          onApplyMonth={() => persistHours("month")}
          saving={saving}
          error={error}
          bookable={!selectedInfo || selectedInfo.bookable}
        />
      </SectionCard>
    </div>
  );
}


/* ── Attendance ─────────────────────────────────────────────────────────── */

/**
 * classify a single attendance record:
 *   "full"   — checked in AND out, duration >= 8 hours
 *   "half"   — checked in AND out but < 8h, OR checked in but not out yet
 *   "absent" — no check-in at all
 */
function classifyDay(rec) {
  if (!rec?.check_in_at) return "absent";
  if (rec.check_in_at && rec.check_out_at) {
    const hrs = (new Date(rec.check_out_at) - new Date(rec.check_in_at)) / 36e5;
    return hrs >= 8 ? "full" : "half";
  }
  return "half"; // checked in but not yet out
}

function hoursWorked(rec) {
  if (!rec?.check_in_at || !rec?.check_out_at) return null;
  return ((new Date(rec.check_out_at) - new Date(rec.check_in_at)) / 36e5).toFixed(1);
}

/* Pure-CSS stacked bar chart */
function AttendanceBarChart({ items, month }) {
  const [year, mon] = month.split("-").map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const today = todayStr();
  const isCurrentMonth = today.slice(0, 7) === month;
  const cutoffDay = isCurrentMonth ? Number(today.slice(8, 10)) : daysInMonth;

  const byDate = {};
  (items || []).forEach((a) => { byDate[a.date] = a; });

  const weeks = [1, 2, 3, 4, 5].map((w) => {
    const start = (w - 1) * 7 + 1;
    const end = Math.min(w * 7, daysInMonth, cutoffDay);
    if (start > cutoffDay) return null;
    let full = 0, half = 0, absent = 0;
    for (let d = start; d <= end; d++) {
      const dateStr = `${month}-${String(d).padStart(2, "0")}`;
      const dow = new Date(dateStr).getDay();
      if (dow === 0 || dow === 6) continue;
      const status = classifyDay(byDate[dateStr]);
      if (status === "full") full++;
      else if (status === "half") half++;
      else absent++;
    }
    const total = full + half + absent;
    return { week: w, start, end: Math.min(w * 7, daysInMonth), full, half, absent, total };
  }).filter(Boolean);

  const maxTotal = Math.max(...weeks.map((w) => w.total), 1);

  return (
    <div className="w-full">
      <div className="flex items-end justify-between gap-2 h-36 px-2">
        {weeks.map((w) => (
          <div key={w.week} className="flex-1 flex flex-col items-center gap-0.5 h-full justify-end group">
            <div className="relative w-full flex flex-col justify-end h-full">
              <div
                className="w-full flex flex-col-reverse rounded-t-xl overflow-hidden transition-all"
                style={{ height: `${Math.max((w.total / maxTotal) * 100, w.total > 0 ? 8 : 2)}%` }}
              >
                {w.absent > 0 && (
                  <div className="w-full bg-red-400" style={{ height: `${(w.absent / w.total) * 100}%` }} />
                )}
                {w.half > 0 && (
                  <div className="w-full bg-amber-400" style={{ height: `${(w.half / w.total) * 100}%` }} />
                )}
                {w.full > 0 && (
                  <div className="w-full bg-emerald-500" style={{ height: `${(w.full / w.total) * 100}%` }} />
                )}
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-start gap-0.5 bg-[#311b52] text-white text-[10px] rounded-lg px-2.5 py-2 shadow-lg z-10 whitespace-nowrap">
                <span className="font-semibold">Week {w.week} (days {w.start}–{w.end})</span>
                <span className="text-emerald-300">✅ Full Day: {w.full}</span>
                <span className="text-amber-300">⏳ Half Day: {w.half}</span>
                <span className="text-red-300">❌ Absent: {w.absent}</span>
              </div>
            </div>
            <span className="text-[10px] text-[#6b7280] mt-1">W{w.week}</span>
            <span className="text-[9px] text-[#9ca3af]">{w.start}–{Math.min(w.end, cutoffDay)}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 justify-center flex-wrap">
        {[
          { color: "bg-emerald-500", label: "Full Day (≥8h)" },
          { color: "bg-amber-400", label: "Half Day (<8h)" },
          { color: "bg-red-400", label: "Absent" },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-[#6b7280]">
            <span className={`w-2.5 h-2.5 rounded-sm ${color}`} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* Month calendar heatmap */
function AttendanceCalendar({ items, month }) {
  const [year, mon] = month.split("-").map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const firstDow = new Date(year, mon - 1, 1).getDay();
  const today = todayStr();
  const isCurrentMonth = today.slice(0, 7) === month;
  const todayDay = Number(today.slice(8, 10));

  const byDate = {};
  (items || []).forEach((a) => { byDate[a.date] = a; });

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${month}-${String(d).padStart(2, "0")}`;
    const rec = byDate[dateStr];
    const dow = new Date(dateStr).getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isFuture = isCurrentMonth && d > todayDay;
    const isToday = isCurrentMonth && d === todayDay;
    const status = isWeekend ? "weekend" : isFuture ? "future" : classifyDay(rec);
    const hrs = hoursWorked(rec);
    cells.push({ d, dateStr, rec, isWeekend, isFuture, isToday, status, hrs });
  }

  const DOW_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const cellStyle = (status, isToday) => {
    const base = "aspect-square flex items-center justify-center rounded-lg text-[11px] font-semibold cursor-default transition-transform hover:scale-110 ";
    const todayRing = isToday ? "ring-2 ring-[#f37721] ring-offset-1 " : "";
    switch (status) {
      case "full":    return base + todayRing + "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300";
      case "half":    return base + todayRing + "bg-amber-100 text-amber-800 ring-1 ring-amber-300";
      case "absent":  return base + todayRing + "bg-red-50 text-red-400 ring-1 ring-red-200";
      case "weekend": return base + "bg-[#f3f4f6] text-[#9ca3af]";
      case "future":  return base + "bg-[#f9fafb] text-[#d1d5db]";
      default:        return base;
    }
  };

  const cellTitle = (cell) => {
    if (cell.isWeekend) return "Weekend";
    if (cell.isFuture) return "Future — not yet";
    if (cell.isToday && cell.status === "future") return "Today";
    switch (cell.status) {
      case "full":   return `Full Day — ${cell.hrs}h worked`;
      case "half":   return cell.rec?.check_out_at
        ? `Half Day — ${cell.hrs}h worked (< 8h)`
        : "Half Day — still checked in";
      case "absent": return "Absent";
      default:       return "";
    }
  };

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW_LABELS.map((l) => (
          <div key={l} className="text-center text-[10px] font-semibold text-[#9ca3af] py-1">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) =>
          cell === null ? (
            <div key={`blank-${i}`} />
          ) : (
            <div
              key={cell.dateStr}
              title={cellTitle(cell)}
              className={cellStyle(cell.status, cell.isToday)}
            >
              {cell.d}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function AttendanceSection() {
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios.get(`${config.API}/nurse/attendance`, { params: { month }, headers: staffHeaders() })
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(detailMessage(err)));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const act = async (kind) => {
    setError("");
    try {
      await axios.post(`${config.API}/nurse/attendance/${kind}`, {}, { headers: staffHeaders() });
      load();
    } catch (err) {
      setError(detailMessage(err));
    }
  };

  const today = data?.today;
  const items = data?.items || [];

  const [year, mon] = month.split("-").map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const todayFull = todayStr();
  const isCurrentMonth = todayFull.slice(0, 7) === month;
  const cutoffDay = isCurrentMonth ? Number(todayFull.slice(8, 10)) : daysInMonth;

  // Count weekdays up to cutoff only
  let weekdays = 0;
  for (let d = 1; d <= cutoffDay; d++) {
    const dow = new Date(year, mon - 1, d).getDay();
    if (dow !== 0 && dow !== 6) weekdays++;
  }

  const byDate = {};
  items.forEach((a) => { byDate[a.date] = a; });

  let fullDays = 0, halfDays = 0, absentDays = 0;
  for (let d = 1; d <= cutoffDay; d++) {
    const dateStr = `${month}-${String(d).padStart(2, "0")}`;
    const dow = new Date(dateStr).getDay();
    if (dow === 0 || dow === 6) continue;
    const status = classifyDay(byDate[dateStr]);
    if (status === "full") fullDays++;
    else if (status === "half") halfDays++;
    else absentDays++;
  }

  const workRate = weekdays > 0 ? Math.round(((fullDays + halfDays * 0.5) / weekdays) * 100) : 0;
  const monthName = new Date(year, mon - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
  const todayHrs = hoursWorked(today);
  const todayStatus = classifyDay(today);

  return (
    <div className="space-y-5">
      {/* Check-in / check-out */}
      <SectionCard title="Today's Attendance" subtitle={`${fmtDate(todayFull)} — 8+ hours counts as a Full Day, less counts as a Half Day.`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] space-y-1">
            <p className="text-sm text-[#311b52] font-semibold">
              {today?.check_in_at ? `Checked in ${fmtDateTime(today.check_in_at)}` : "Not checked in yet"}
            </p>
            {today?.check_out_at && (
              <p className="text-xs text-[#6b7280]">
                Checked out {fmtDateTime(today.check_out_at)}{todayHrs && ` · ${todayHrs}h worked`}
              </p>
            )}
            {today?.check_in_at && today?.check_out_at && (
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${todayStatus === "full" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {todayStatus === "full" ? "✅ Full Day" : "⏳ Half Day"}
              </span>
            )}
          </div>
          {!today?.check_in_at && (
            <button type="button" onClick={() => act("check-in")}
              className="px-5 py-2.5 rounded-full bg-[#f37721] text-white text-sm font-semibold" data-testid="check-in-btn">
              Check in
            </button>
          )}
          {today?.check_in_at && !today?.check_out_at && (
            <button type="button" onClick={() => act("check-out")}
              className="px-5 py-2.5 rounded-full bg-[#311b52] text-white text-sm font-semibold" data-testid="check-out-btn">
              Check out
            </button>
          )}
          {today?.check_in_at && today?.check_out_at && (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="w-4 h-4" /> Attendance marked
            </span>
          )}
        </div>
        {error && <p className="mt-3 text-sm text-[#ee4036]">{error}</p>}
      </SectionCard>

      {/* Month selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Monthly Tracker</h2>
          <p className="text-sm text-[#6b7280]">
            {monthName}{isCurrentMonth ? ` · showing up to today (day ${cutoffDay})` : ""}
          </p>
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-xl border border-[#673c96]/20 px-3 py-2 text-sm" data-testid="attendance-month" />
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Full Days", value: fullDays, sub: "≥ 8 hrs worked", color: "from-emerald-500 to-green-600", icon: "✅" },
          { label: "Half Days", value: halfDays, sub: "< 8 hrs worked", color: "from-amber-400 to-orange-500", icon: "⏳" },
          { label: "Absent", value: absentDays, sub: "working days missed", color: "from-red-400 to-rose-600", icon: "❌" },
          { label: "Work Rate", value: `${workRate}%`, sub: "full+½ of weekdays", color: "from-[#673c96] to-[#311b52]", icon: "📊" },
        ].map(({ label, value, sub, color, icon }) => (
          <div key={label} className={`bg-gradient-to-br ${color} rounded-2xl p-4 text-white shadow-sm`}>
            <div className="text-2xl mb-1">{icon}</div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs font-semibold opacity-90 mt-0.5">{label}</p>
            <p className="text-[10px] opacity-70">{sub}</p>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] p-5 shadow-sm">
        <p className="text-sm font-bold text-[#311b52] mb-1" style={{ fontFamily: "Poppins" }}>Weekly Breakdown</p>
        <p className="text-xs text-[#9ca3af] mb-4">Hover over a bar to see the breakdown · only weeks up to today are counted</p>
        <AttendanceBarChart items={items} month={month} />
      </div>

      {/* Calendar heatmap */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-sm font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Month View — {monthName}</p>
            <p className="text-[10px] text-[#9ca3af]">Today is highlighted with an orange ring · future dates are greyed out</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { color: "bg-emerald-100 ring-1 ring-emerald-300", label: "Full Day" },
              { color: "bg-amber-100 ring-1 ring-amber-300", label: "Half Day" },
              { color: "bg-red-50 ring-1 ring-red-200", label: "Absent" },
              { color: "bg-[#f3f4f6]", label: "Weekend" },
              { color: "bg-[#f9fafb] ring-1 ring-gray-200", label: "Future" },
            ].map(({ color, label }) => (
              <span key={label} className="flex items-center gap-1 text-[10px] text-[#6b7280]">
                <span className={`w-3 h-3 rounded ${color}`} />{label}
              </span>
            ))}
          </div>
        </div>
        <AttendanceCalendar items={items} month={month} />
      </div>

      {/* Detailed history */}
      <SectionCard title="Detailed History" actions={
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-xl border border-[#673c96]/20 px-3 py-2 text-sm" />
      }>
        {items.length === 0 ? (
          <p className="text-sm text-[#6b7280]">No attendance records for this month.</p>
        ) : (
          <div className="divide-y divide-[#673c96]/10">
            {items.map((a) => {
              const hrs = hoursWorked(a);
              const status = classifyDay(a);
              return (
                <div key={a.date} className="py-2.5 flex items-center justify-between text-sm gap-2">
                  <span className="font-medium text-[#311b52]">{fmtDate(a.date)}</span>
                  <span className="text-[#6b7280] text-xs">
                    {a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "—"}
                    {" → "}
                    {a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "—"}
                    {hrs && ` (${hrs}h)`}
                  </span>
                  {status === "full"
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">Full Day</span>
                    : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">Half Day</span>}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ── Profile ────────────────────────────────────────────────────────────── */

function ProfileSection() {
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${config.API}/nurse/profile`, { headers: staffHeaders() })
      .then(({ data }) => setForm(data))
      .catch((err) => setError(detailMessage(err)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setError("");
    try {
      const err = fieldError({
        name: form.full_name,
        password: password.trim() ? password : undefined,
      });
      if (err) { setError(err); return; }
      const body = {
        full_name: form.full_name,
        qualification: form.qualification,
        uniform_size: form.uniform_size,
        status: form.status,
      };
      const hadPassword = Boolean(password.trim());
      if (hadPassword) body.password = password.trim();
      await axios.patch(`${config.API}/nurse/profile`, body, { headers: staffHeaders() });
      setMsg(hadPassword ? "Profile saved — password set. You can now log in with email & password too." : "Profile saved");
      setPassword("");
      setChangingPassword(false);
      if (hadPassword) setForm((f) => ({ ...f, has_password: true }));
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;
  }

  const onDuty = form.status !== "off_duty";

  return (
    <SectionCard title="My profile" subtitle="Basic settings — uniform, duty status and password.">
      <form onSubmit={save} className="space-y-4 max-w-lg">
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Full name</label>
          <input value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="profile-name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Email (login)</label>
          <input value={form.email || ""} disabled className="w-full rounded-xl border border-[#673c96]/10 bg-[#f4f5f7] px-4 py-3 text-[#6b7280]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Phone (OTP login)</label>
          <input value={form.phone || ""} disabled className="w-full rounded-xl border border-[#673c96]/10 bg-[#f4f5f7] px-4 py-3 text-[#6b7280]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Qualification</label>
          <input value={form.qualification || ""} onChange={(e) => setForm({ ...form, qualification: e.target.value })}
            className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-1">Uniform size</label>
          <div className="flex flex-wrap gap-2">
            {UNIFORM_SIZES.map((s) => (
              <button key={s} type="button" onClick={() => setForm({ ...form, uniform_size: s })}
                className={`px-4 py-2 rounded-full text-sm font-semibold border ${form.uniform_size === s ? "bg-[#673c96] text-white border-[#673c96]" : "border-[#673c96]/25 text-[#311b52]"}`}
                data-testid={`uniform-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between bg-[#faf8fc] rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-[#311b52]">Available for duty</p>
            <p className="text-xs text-[#6b7280]">Turn off if you're on leave — ops won't assign you new visits.</p>
          </div>
          <button type="button" onClick={() => setForm({ ...form, status: onDuty ? "off_duty" : "available" })}
            className={`w-12 h-7 rounded-full transition-colors relative ${onDuty ? "bg-emerald-500" : "bg-gray-300"}`}
            data-testid="duty-toggle">
            <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${onDuty ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="text-xs font-semibold text-[#311b52]">Password</label>
            {form.has_password ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="password-set-badge">
                <CheckCircle2 className="w-3 h-3" /> Saved
              </span>
            ) : (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not set</span>
            )}
          </div>
          {form.has_password && !changingPassword ? (
            <>
              <input type="password" readOnly value="••••••••••••" tabIndex={-1}
                className="w-full rounded-xl border border-[#673c96]/10 bg-[#f4f5f7] px-4 py-3 text-[#311b52] tracking-widest"
                data-testid="profile-password-masked" />
              <button type="button" onClick={() => setChangingPassword(true)}
                className="mt-2 text-sm font-semibold text-[#673c96] underline" data-testid="change-password-btn">
                Change password
              </button>
            </>
          ) : (
            <>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8}
                placeholder={form.has_password ? "Enter a new password (min 8 characters)" : "Min 8 characters"}
                className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="profile-password" />
              {form.has_password && (
                <button type="button" onClick={() => { setChangingPassword(false); setPassword(""); }}
                  className="mt-2 text-sm text-[#6b7280]">Cancel</button>
              )}
            </>
          )}
          <p className="text-[11px] text-[#6b7280] mt-1">
            {form.has_password
              ? "Your password is saved. We never show it back for security — use Change password to update it."
              : "Set a password to log in with your email as well as phone OTP."}
          </p>
        </div>
        {msg && <p className="text-sm text-emerald-700">{msg}</p>}
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        <button disabled={saving} className="px-6 py-3 rounded-full bg-[#f37721] text-white font-semibold" data-testid="profile-save">
          {saving ? "Saving…" : "Save profile"}
        </button>
      </form>
    </SectionCard>
  );
}

/* ── Overview ───────────────────────────────────────────────────────────── */

function IceboxCard({ onChanged }) {
  const [data, setData] = useState(null);
  const [deviceId, setDeviceId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${config.API}/nurse/icebox`, { headers: staffHeaders() })
      .then(({ data: d }) => {
        setData(d);
        setDeviceId((prev) => prev || d?.assignment?.device_id || "");
      })
      .catch((err) => setError(detailMessage(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (path, extra) => {
    setBusy(true); setError("");
    try {
      await axios.post(`${config.API}${path}`, extra || {}, { headers: staffHeaders() });
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(detailMessage(err));
    } finally { setBusy(false); }
  };

  const asg = data?.assignment;
  const dev = data?.device || {};

  return (
    <SectionCard title="Icebox / cold chain" subtitle="Bind the Go logger in your vaccine box, then leave office.">
      <div className="space-y-3" data-testid="nurse-icebox-card">
        <div className="flex items-start gap-3">
          <Thermometer className="w-5 h-5 text-[#673c96] mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <IceboxTempBar temp={dev.last_temp} lastUpdated={dev.last_packet_at || data?.last_updated} />
            <div className="flex flex-wrap gap-3 mt-1">
              {dev.battery != null && <p className="text-xs text-[#6b7280]">Battery {dev.battery}%</p>}
              {dev.lid_open && <p className="text-xs font-semibold text-amber-700">Lid open</p>}
            </div>
          </div>
        </div>
        <p className="text-xs text-[#6b7280]">
          {asg ? `Today: ${asg.device_id} · ${(asg.status || "").replace("_", " ")}` : "No icebox bound for today."}
        </p>
        {!asg && (
          <div className="flex flex-wrap gap-2">
            <input value={deviceId} onChange={(e) => setDeviceId(e.target.value)} placeholder="Device ID e.g. AA3630"
              list="icebox-devices" className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border border-[#673c96]/20 text-sm" />
            <datalist id="icebox-devices">
              {(data?.devices || []).map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </datalist>
            <button type="button" disabled={busy || !deviceId.trim()}
              onClick={() => act("/nurse/icebox/bind", { device_id: deviceId.trim() })}
              className="px-4 py-2 rounded-full bg-[#673c96] text-white text-sm font-semibold">Bind</button>
          </div>
        )}
        {asg && asg.status !== "in_transit" && (
          <button type="button" disabled={busy} onClick={() => act("/nurse/icebox/leave-office")}
            className="px-4 py-2.5 rounded-full bg-[#f37721] text-white text-sm font-semibold" data-testid="btn-leave-office">
            Leave office
          </button>
        )}
        {asg?.status === "in_transit" && (
          <button type="button" disabled={busy} onClick={() => act("/nurse/icebox/end-trip")}
            className="px-4 py-2 rounded-full border border-[#673c96]/30 text-[#673c96] text-sm font-semibold">
            End icebox trip
          </button>
        )}
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
      </div>
    </SectionCard>
  );
}

function OverviewSection({ user, goTo }) {
  const [today, setToday] = useState({ items: [], today: [] });
  const [attendance, setAttendance] = useState(null);
  const [attendanceReady, setAttendanceReady] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [avail, setAvail] = useState(null);

  const load = useCallback(() => {
    axios.get(`${config.API}/nurse/today`, { headers: staffHeaders() })
      .then(({ data }) => setToday(data))
      .catch(() => {});
    axios.get(`${config.API}/nurse/attendance`, { headers: staffHeaders() })
      .then(({ data }) => setAttendance(data.today || {}))
      .catch(() => setAttendance({}))
      .finally(() => setAttendanceReady(true));
    axios.get(`${config.API}/nurse/availability`, { headers: staffHeaders() })
      .then(({ data }) => setAvail(data))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkIn = async () => {
    try {
      await axios.post(`${config.API}/nurse/attendance/check-in`, {}, { headers: staffHeaders() });
      load();
    } catch { /* surfaced in attendance tab */ }
  };

  const todays = today.today?.length ? today.today : (today.items || []).filter((b) => b.preferred_date === todayStr());
  const ongoing = (today.items || []).filter((b) => b.status === "ongoing");
  const firstName = (user?.full_name || "nurse").split(" ")[0];

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-[#673c96] to-[#311b52] rounded-2xl p-5 text-white flex flex-wrap items-center gap-3">
        <Clock4 className="w-6 h-6 text-[#f8b133]" />
        <div className="flex-1 min-w-[180px]">
          <p className="font-semibold" style={{ fontFamily: "Poppins" }}>{greetingForNow()}, {firstName}!</p>
          {!attendanceReady && <p className="text-sm text-white/80">Loading attendance…</p>}
          {attendanceReady && !attendance?.check_in_at && (
            <p className="text-sm text-white/80">Check in to mark today's attendance.</p>
          )}
          {attendanceReady && attendance?.check_in_at && (
            <p className="text-sm text-white/80">Checked in {fmtDateTime(attendance.check_in_at)}</p>
          )}
        </div>
        {attendanceReady && !attendance?.check_in_at && (
          <button type="button" onClick={checkIn}
            className="px-5 py-2.5 rounded-full bg-[#f37721] text-white text-sm font-semibold" data-testid="overview-checkin">
            Check in now
          </button>
        )}
      </div>

      <IceboxCard onChanged={load} />

      {avail?.notice && (
        <button
          type="button"
          onClick={() => goTo("availability")}
          className="w-full text-left rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3"
          data-testid="overview-hours-notice"
        >
          <p className="text-sm font-semibold text-emerald-900">Your hours were updated</p>
          <p className="text-sm text-emerald-800 mt-0.5">{avail.notice}</p>
          <p className="text-xs font-semibold text-[#673c96] mt-1">Open My Availability</p>
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Today's visits" value={todays.length} accent />
        <StatCard label="Ongoing" value={ongoing.length} />
        <StatCard label="Upcoming assigned" value={(today.items || []).filter((b) => !b.date_passed).length} />
      </div>

      {Array.isArray(avail?.schedule) && avail.schedule.length > 0 && (
        <SectionCard
          title="Upcoming hours"
          subtitle="Open vs booked"
          actions={<button type="button" onClick={() => goTo("availability")} className="text-sm font-semibold text-[#673c96] underline">All slots</button>}
        >
          <div className="space-y-2">
            {[...avail.schedule].sort((a, b) => `${a.date}${a.hour}`.localeCompare(`${b.date}${b.hour}`)).slice(0, 6).map((row) => (
              <div key={`${row.date}-${row.hour}`} className="flex items-center gap-3 text-sm">
                <span className="min-w-[88px] font-semibold text-[#311b52]">{fmtDate(row.date)}</span>
                <span className="flex-1 text-[#311b52]">{row.label || row.hour}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${row.status === "booked" ? "bg-orange-100 text-orange-800" : "bg-emerald-100 text-emerald-800"}`}>
                  {row.status === "booked" ? "Booked" : "Open"}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Today's schedule" subtitle={fmtDate(todayStr())}
        actions={<button type="button" onClick={() => goTo("bookings")} className="text-sm font-semibold text-[#673c96] underline">All bookings</button>}>
        {todays.length === 0 ? (
          <p className="text-sm text-[#6b7280]">No visits scheduled for today.</p>
        ) : (
          <div className="grid gap-3">{todays.map((b) => <BookingCard key={b.id} b={b} onOpen={setOpenId} />)}</div>
        )}
      </SectionCard>

      {openId && <BookingDetail bookingId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

/* ── Portal root ────────────────────────────────────────────────────────── */

export default function NursePortal() {
  const session = useStaffSession("nurse");
  const [tab, setTab] = useState("overview");

  if (session.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f5f7]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />
      </div>
    );
  }

  if (session.status === "login") {
    return (
      <StaffLogin role="nurse" title="Nurse portal" onAuthed={session.onAuthed}
        subtitle="Sign in with your registered phone to see today's visits, availability and attendance." />
    );
  }

  const nav = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "bookings", label: "My Bookings", icon: ClipboardList },
    { id: "availability", label: "My Availability", icon: CalendarDays },
    { id: "attendance", label: "Attendance", icon: Clock4 },
    { id: "profile", label: "Profile", icon: UserRound },
  ];

  return (
    <StaffShell roleLabel="Nurse" user={session.user} nav={nav} active={tab} onNavigate={setTab} onLogout={session.logout} portalPath="/nurse" onProfileClick={() => setTab("profile")}>
      {tab === "overview" && <OverviewSection user={session.user} goTo={setTab} />}
      {tab === "bookings" && <BookingsSection />}
      {tab === "availability" && <AvailabilitySection />}
      {tab === "attendance" && <AttendanceSection />}
      {tab === "profile" && <ProfileSection />}
    </StaffShell>
  );
}
